import style from "./TalisCampaignDetail.module.scss";
import { useEffect, useState } from "react";
import {
    ipcClaimReferralDeposit,
    ipcClaimParticipationReward,
} from "../../../../libs/api/campaign/execution";
import { talisRequirement } from "../../../../libs/api/campaign/query";
import CampaignDetailPoolHistory from "../../../common/CampaignComponents/CampaignDetailPoolHistory";

import { multiply } from "../../../../libs/Math";
import TxBoxView from "../../../common/TxBoxView/TxBoxView";
import { useWallet } from "@terra-money/wallet-provider";
import { useRecoilValue } from "recoil";
import { blockState, walletState } from "../../../../libs/contexts";
import classNames from "classnames/bind";
import TalisInfo from "./Info/TalisInfo";
import TalisParticipation from "./Participation/TalisParticipation";
import TalisCampaignAbout from "./About/TalisCampaignAbout";
import IPCDetailTitle from "./IPC/Title/IPCDetailTitle";
import TalisGuide from "./Guide/TalisGuide";
import TalisReward from "./Reward/TalisReward";
import TalisStandbyBanner from "./StandbyBanner/TalisStandbyBanner";
import CampaignDescription from "../Common/CampaignDescription";
import MySharing from "./MySharing";
import MyRewards from "./MyRewards";
import { joinCampaignV2 } from "../../../../libs/api/campaign/qualification/execution";
import { environment } from "../../../../libs/api/api";

const cx = classNames.bind(style);

export default function TalisCampaignDetail(props: {
    campaignAddress: string;
    summary: CampaignSummary;
    earlybird: boolean;
    refresh: () => void;
}) {
    const { post } = useWallet();
    const wallet = useRecoilValue(walletState);
    const block = useRecoilValue(blockState);

    const [hash, setHash] = useState<HashResult | undefined>(undefined);

    const [participated, setParticipated] = useState(false);
    const [requirement, setRequirement] = useState<
        TalisRequirement | undefined
    >(undefined);
    const [started, setStarted] = useState(false);

    useEffect(() => {
        if (
            props.summary &&
            props.summary.participation &&
            props.summary.participation.last_participated_at > 0
        ) {
            setParticipated(true);
        } else {
            setParticipated(false);
        }
    }, [props.summary]);

    useEffect(() => {
        talisRequirement(props.earlybird)
            .then((r) => {
                setRequirement(r);
            })
            .catch((e) => {});
    }, [props.earlybird]);

    useEffect(() => {
        if (requirement) {
            if (block.block >= requirement.start_block) {
                setStarted(true);
            }
        }
    }, [block, requirement]);

    function ipcParticipate(
        token: string,
        referrer: string | undefined,
        txHash: string,
        signature: string,
        ruleId: string
    ) {
        if (wallet) {
            setHash({
                type: "IPCParticipate",
                isPending: true,
            });
            const qualifier = props.earlybird
                ? environment().contracts.talis_early.qualifier
                : environment().contracts.talis_public.qualifier;

            joinCampaignV2(
                props.campaignAddress,
                qualifier,
                wallet!.terraAddress,
                multiply(token, 1000000),
                txHash,
                signature,
                ruleId,
                referrer
            )
                .then((r) => {
                    return post(r);
                })
                .then((r) => {
                    setHash({
                        type: "IPCParticipate",
                        response: r,
                        redirect: undefined,
                        callback: (success: boolean) => {
                            if (success) {
                                props.refresh();
                            }
                        },
                    });
                })
                .catch((e) => {
                    setHash({
                        type: "IPCParticipate",
                        error: e,
                        redirect: undefined,
                    });
                });
        }
    }

    function claimParticipationReward() {
        if (props.campaignAddress && wallet) {
            setHash({
                type: "IPCClaim",
                isPending: true,
            });

            ipcClaimParticipationReward(
                props.campaignAddress,
                wallet.terraAddress
            )
                .then((r) => {
                    return post(r);
                })
                .then((r) => {
                    setHash({
                        type: "IPCClaim",
                        response: r,
                        redirect: undefined,
                    });
                })
                .catch((e) => {
                    setHash({
                        type: "IPCClaim",
                        error: e,
                        redirect: undefined,
                    });
                });
        }
    }

    function claimReferralDepositReward() {
        setHash({
            type: "CampaignClaimOne",
            isPending: true,
        });

        ipcClaimReferralDeposit(props.campaignAddress, wallet!.terraAddress)
            .then((r) => {
                return post(r);
            })
            .then((response) => {
                setHash({
                    type: "CampaignClaimOne",
                    response: response,
                    redirect: undefined,
                });
            })
            .catch((error) => {
                setHash({
                    type: "CampaignClaimOne",
                    error: error,
                    redirect: undefined,
                });
            });
    }

    return (
        <div className={style.wrap}>
            {hash === undefined ? <div className={style.bg_footer} /> : <div />}

            <div className={cx(style.container, { hash: hash !== undefined })}>
                <TxBoxView
                    hash={hash}
                    clearHash={() => {
                        setHash(undefined);
                    }}
                    transparent={true}
                >
                    {!started ? (
                        <div className={style.ipcbeforeopen}>
                            <TalisStandbyBanner
                                earlybird={props.earlybird}
                                requirement={requirement}
                            />
                        </div>
                    ) : (
                        <div />
                    )}

                    <IPCDetailTitle
                        earlybird={props.earlybird}
                        summary={props.summary}
                        started={started}
                    />

                    {participated === true ? (
                        <TalisReward
                            summary={props.summary}
                            claimParticipationReward={claimParticipationReward}
                        />
                    ) : (
                        <div />
                    )}

                    {participated ? (
                        <div className={style.header_container}>
                            <MyRewards
                                summary={props.summary}
                                campaignAddress={props.campaignAddress}
                                claimPressed={claimReferralDepositReward}
                            />
                            <MySharing
                                summary={props.summary}
                                campaignAddress={props.campaignAddress}
                            />
                        </div>
                    ) : (
                        <div />
                    )}
                    <TalisGuide
                        earlybird={props.earlybird}
                        requirement={requirement}
                        summary={props.summary}
                    />
                    {participated === true ? (
                        <div />
                    ) : (
                        <TalisParticipation
                            summary={props.summary}
                            earlybird={props.earlybird}
                            campaignAddress={props.campaignAddress}
                            requirement={requirement}
                            started={started}
                            ipcParticipate={ipcParticipate}
                        />
                    )}
                    {participated === true ? (
                        <div style={{ marginTop: 40 }} />
                    ) : (
                        <div />
                    )}
                    <TalisInfo
                        campaignAddress={props.campaignAddress}
                        summary={props.summary}
                    />

                    <TalisCampaignAbout />

                    <CampaignDescription
                        summary={props.summary}
                        isIpc={true}
                        title={"Talis"}
                    />
                    <CampaignDetailPoolHistory
                        campaignAddress={props.campaignAddress}
                    />
                </TxBoxView>
            </div>
        </div>
    );
}
